import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';
import DeletionRequestModal from './DeletionRequestModal';
import ActionsDropdown from './ActionsDropdown';
import FlyerHistoryTabs, { MultiEventFlyerCard, FlyerPagination, S as FlyerS, Smobile as FlyerSmobile, FLYER_PAGE_SIZE, FLYER_COLS } from './FlyerHistoryTabs';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// ── Flyers tab content for a Festival Series ──────────────────────────────────
// A series always has a parent relationship to its children — its own Flyers
// tab wouldn't be useful with the standard three sub-tabs (Show Flyer History
// and Shows Without Flyers would always be empty, since a series never has
// its own events). Instead, this flattens each child festival's own Festival
// Flyer History images into one grid, tagged with which child they belong to.
function SeriesFlyerHistoryTab({ childFestivals }) {
    const isMobile = useIsMobile();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);

    useEffect(() => {
        let cancelled = false;
        async function loadAll() {
            if (!childFestivals || childFestivals.length === 0) {
                setImages([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const token = await getIdToken();
                const results = await Promise.all(childFestivals.map(cf =>
                    axios.get(`${config.API_URL}/images`, {
                        params: { entityType: 'festival', entityId: cf.id, mode: 'flyer-calendar', sort: 'desc' },
                        headers: { Authorization: token },
                    })
                        .then(res => (res.data.images || []).map(img => ({ ...img, _childFestival: cf })))
                        .catch(() => [])
                ));
                if (!cancelled) setImages(results.flat());
            } catch {
                if (!cancelled) setError('Failed to load flyer images.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        loadAll();
        return () => { cancelled = true; };
    }, [childFestivals]);

    const latestDate = (img) => {
        const dates = (img.linked_events || []).map(e => e.event_date).filter(Boolean).sort();
        return dates[dates.length - 1] || '';
    };
    const sorted = [...images].sort((a, b) => {
        const cmp = latestDate(a) < latestDate(b) ? -1 : latestDate(a) > latestDate(b) ? 1 : 0;
        return sortDir === 'desc' ? -cmp : cmp;
    });

    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount = remainder === 0 ? 0 : FLYER_COLS - remainder;

    if (loading) return <div style={FlyerS.emptyTab}>Loading…</div>;
    if (error)   return <div style={FlyerS.emptyTab}>{error}</div>;

    if (sorted.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🖼️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Festival Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Festival Flyer History images from child festivals will appear here once uploaded.</div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={FlyerS.flyerSortGroup}>
                    <label style={FlyerS.flyerSortLabel}>Sort by</label>
                    <select value={sortDir} onChange={e => { setSortDir(e.target.value); setPage(1); }} style={FlyerS.flyerSortSelect}>
                        <option value="desc">Date (Most Recent to Oldest)</option>
                        <option value="asc">Date (Oldest to Most Recent)</option>
                    </select>
                </div>
            </div>
            <div style={isMobile ? { ...FlyerS.flyerGrid, ...FlyerSmobile.flyerGrid } : FlyerS.flyerGrid}>
                {paginated.map(img => (
                    <MultiEventFlyerCard
                        key={img.id}
                        image={img}
                        viewFestivalTo={`/festivals/${img._childFestival.slug || img._childFestival.id}`}
                        festivalLabel={img._childFestival.name}
                    />
                ))}
                {Array.from({ length: padCount }).map((_, i) => <div key={`pad-${i}`} style={FlyerS.flyerCellPad} />)}
            </div>
            <FlyerPagination currentPage={page} totalItems={sorted.length} onPageChange={p => setPage(p)} />
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                {sorted.length} image{sorted.length !== 1 ? 's' : ''}
                {Math.ceil(sorted.length / FLYER_PAGE_SIZE) > 1 ? ` · Page ${page} of ${Math.ceil(sorted.length / FLYER_PAGE_SIZE)}` : ''}
            </div>
        </div>
    );
}

function FestivalDetail({ slugOverride } = {}) {
    const isMobile = useIsMobile();
    const params = useParams();
    const navigate = useNavigate();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [photosOpen, setPhotosOpen] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [photosError, setPhotosError] = useState('');
    const [photosLoaded, setPhotosLoaded] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [expandedChildFestival, setExpandedChildFestival] = useState(null);

    // Attended state
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (eventList) => {
        if (!eventList || eventList.length === 0) return;
        try {
            const eventIds = eventList.map(e => e.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const attendedMap2 = {};
            const performedMap2 = {};
            const workedMap2 = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                attendedMap2[id] = status.attended;
                performedMap2[id] = status.performed || false;
                workedMap2[id] = status.worked || false;
            });
            setAttendedMap(attendedMap2);
            setPerformedMap(performedMap2);
            setWorkedMap(workedMap2);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    useEffect(() => {
        // Client-side navigation between two festival pages (e.g. clicking a
        // child festival from a series' Child Festivals tab) reuses this same
        // component instance rather than remounting it — slug changes, but
        // React-state like activeTab doesn't reset on its own. Without this,
        // a tab index that doesn't exist on the new festival (e.g. landing on
        // "Child Festivals" when the new festival isn't itself a series)
        // renders nothing, which reads as "events missing" until a hard refresh.
        setActiveTab(0);
        setSearch('');
        setPhotosOpen(false);
        setPhotos([]);
        setPhotosLoaded(false);
        setPhotosError('');
        setLightbox(null);
        setExpandedChildFestival(null);

        async function load() {
            try {
                const res = await axios.get(`${config.API_URL}/festivals/${slug}`);
                setData(res.data);
                setLoading(false);
                setDefaultImageId(null);
                setDefaultImageUrl(null);
                setDefaultImageResolving(false);
                if (res.data?.festival?.default_image_id) {
                    setDefaultImageId(res.data.festival.default_image_id);
                    setDefaultImageResolving(true);
                    try {
                        const token = await getIdToken();
                        const imgRes = await axios.get(`${config.API_URL}/images`, {
                            params: { entityType: 'festival', entityId: res.data.festival.id },
                            headers: { Authorization: token },
                        });
                        const imgs = imgRes.data.images || [];
                        const defaultImg = imgs.find(i => i.id === res.data.festival.default_image_id);
                        if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                    } catch { /* fall back to festival.image_url */ }
                    finally { setDefaultImageResolving(false); }
                }
                if (res.data && res.data.festival) {
                    document.title = `SHARC - ${res.data.festival.name}`;
                }
                if (res.data && res.data.events) {
                    loadAttendedStatus(res.data.events);
                }
            } catch {
                setError('Failed to load festival');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    const togglePhotos = async () => {
        const opening = !photosOpen;
        setPhotosOpen(opening);
        if (opening && !photosLoaded && data) {
            setPhotosLoading(true);
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/images`, {
                    params: { entityType: 'festival', entityId: data.festival.id },
                    headers: { Authorization: token },
                });
                setPhotos(res.data.images || []);
                setPhotosLoaded(true);
            } catch {
                setPhotosError('Failed to load photos.');
            } finally {
                setPhotosLoading(false);
            }
        }
    };

    const handleSetDefault = async (image) => {
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'festival', entityId: data.festival.id },
                { headers: { Authorization: token } }
            );
            setDefaultImageId(image.id);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        }
    };

    const handleRemoveDefault = async () => {
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'festival', entityId: data.festival.id },
                { headers: { Authorization: token } }
            );
            setDefaultImageId(null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        }
    };

    if (loading) return <div style={styles.message}>Loading festival...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { festival, events, promoters, has_existing_events, pending_deletion, parent_festival, child_festivals, child_festival_events } = data;
    const TABS = festival.is_parent ? ['Events', 'Flyers', 'Child Festivals'] : ['Events', 'Flyers'];

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const formatDateLong = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const formatRequestedAt = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const start = formatDate(festival.start_date);
    const end = formatDate(festival.end_date);
    const dateRange = start && end && start !== end
        ? `${start} – ${end}`
        : start || null;

    const q = normalizeStr(search);
    const filtered = search
        ? events.filter(e => [
            e.event_date ? formatDateLong(e.event_date) : '',
            e.venue_name, e.city, e.state, e.country, e.artists
          ].some(v => v && normalizeStr(v).includes(q)))
        : events;

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pending_deletion ? '12px' : '24px' }}>
                <Link to="/festivals" style={styles.backLink}>← Back to Festivals</Link>
                {!pending_deletion && (
                    <ActionsDropdown
                        editPath={`/festivals/${festival.slug || festival.id}/edit`}
                        editLabel="Edit Festival"
                        onDeleteClick={() => setShowDeleteModal(true)}
                        deleteLabel="Delete Festival"
                        deleteDisabled={has_existing_events}
                        deleteTooltip="This festival cannot be deleted while it is tied to an existing event. Remove it from all events first."
                    />
                )}
            </div>

            {pending_deletion && (
                <div style={styles.pendingDeletionBanner}>
                    <span style={styles.pendingDeletionIcon}>🗑️</span>
                    <span>
                        Festival marked for deletion by <strong>{pending_deletion.requested_by}</strong> on{' '}
                        <strong>{formatRequestedAt(pending_deletion.requested_at)}</strong>
                        {pending_deletion.reason && (
                            <> — <em>{pending_deletion.reason}</em></>
                        )}
                    </span>
                </div>
            )}

            {showDeleteModal && (
                <DeletionRequestModal
                    entityType="festival"
                    entityId={festival.id}
                    entityName={festival.name}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => navigate('/festivals')}
                />
            )}

            {/* Header */}
            <div style={styles.header}>
                <div style={(defaultImageResolving ? false : (defaultImageUrl || festival.image_url)) ? styles.headerWithImage : undefined}>
                    {!defaultImageResolving && (defaultImageUrl || festival.image_url) && (
                        <img
                            src={defaultImageUrl || festival.image_url}
                            alt={festival.name}
                            style={styles.festivalImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{festival.name}{festival.is_parent ? ' (Series)' : ''}</h1>
                        {parent_festival && (
                            <div style={styles.parentRow}>
                                Part of <Link to={`/festivals/${parent_festival.slug || parent_festival.id}`} style={styles.promoterLink}>{parent_festival.name}</Link>
                            </div>
                        )}
                        {dateRange && <div style={styles.dateRange}>{dateRange}</div>}
                        {promoters && promoters.length > 0 && (
                            <div style={styles.promoterRow}>
                                {promoters.map((p, i) => (
                                    <span key={p.id}>
                                        <Link to={`/promoters/${p.slug}`} style={styles.promoterLink}>{p.name}</Link>
                                        {i < promoters.length - 1 && <span style={{ color: '#555', margin: '0 4px' }}>·</span>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {festival.description && (
                    <div style={styles.bio}>{festival.description}</div>
                )}
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{events.length}</div>
                    <div style={styles.statLabel}>Events</div>
                </div>
                {festival.is_parent && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{child_festivals.length}</div>
                        <div style={styles.statLabel}>Child Festivals</div>
                    </div>
                )}
            </div>

            {/* Photos — collapsible */}
            <div style={styles.collapsibleSection}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button onClick={togglePhotos} style={{ ...styles.collapsibleHeader, flex: 1 }}>
                        <span style={styles.collapsibleTitle}>📷 Photos</span>
                        <span style={styles.collapsibleChevron}>{photosOpen ? '▲' : '▼'}</span>
                    </button>
                    {defaultImageId && (
                        <button onClick={handleRemoveDefault} style={styles.removeDefaultBtn}>
                            ✕ Remove Default
                        </button>
                    )}
                    <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                        + Add Photo
                    </button>
                </div>
                {photosOpen && (
                    <div style={styles.collapsibleBody}>
                        {photosLoading && <div style={styles.photosEmpty}>Loading photos…</div>}
                        {photosError && <div style={styles.photosEmpty}>{photosError}</div>}
                        {!photosLoading && !photosError && photos.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📷</div>
                                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No photos yet for this festival.</div>
                            </div>
                        )}
                        {!photosLoading && photos.length > 0 && (
                            <div style={styles.photoGrid}>
                                {photos.map(img => {
                                    const isDefault = img.id === defaultImageId;
                                    return (
                                        <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? { borderColor: '#bb86fc' } : {}) }}>
                                            <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                                <img src={img.image_url} alt={img.caption || ''} style={styles.photoThumbImg} loading="lazy" />
                                                {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                                {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                            </div>
                                            <div style={styles.photoCardBody}>
                                                {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                                {img.is_public && !isDefault && (
                                                    <button onClick={() => handleSetDefault(img)} style={styles.setDefaultBtn}>
                                                        ★ Set as Default
                                                    </button>
                                                )}
                                                {isDefault && <div style={{ fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' }}>Current default</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={(img) => {
                        setPhotos(prev => [img, ...prev]);
                        setPhotosLoaded(true);
                        setPhotosOpen(true);
                        setShowUpload(false);
                    }}
                    presetEntityType="festival"
                    presetEntityId={festival.id}
                    presetEntityName={festival.name}
                />
            )}

            {/* Lightbox */}
            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={isMobile ? { ...styles.tabBar, ...Smobile.tabBar } : styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => { setActiveTab(i); setSearch(''); }}
                            style={{
                                ...styles.tabBtn,
                                ...(activeTab === i ? styles.tabBtnActive : {}),
                                ...(isMobile ? Smobile.tabBtn : {}),
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div style={styles.tabContent}>
                    {/* ── Events tab ── */}
                    {activeTab === 0 && (
                        events.length === 0 ? (
                            <div style={styles.emptyTab}>No events linked to this festival yet.</div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="Search by date, venue, city, artists..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={styles.tabSearch}
                                />
                                {filtered.length === 0 ? (
                                    <div style={styles.emptyTab}>No events match your search.</div>
                                ) : (
                                    <div style={styles.tableContainer}>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.th}>Date</th>
                                                    <th style={styles.th}>City</th>
                                                    <th style={styles.th}>State</th>
                                                    <th style={styles.th}>Country</th>
                                                    <th style={styles.th}>Venue</th>
                                                    <th style={styles.th}>Artists</th>
                                                    <th style={styles.th}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filtered.map((e, idx) => {
                                                    const dateObj = e.event_date ? new Date(e.event_date) : null;
                                                    const formattedDate = dateObj
                                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                                        : '—';
                                                    const isAttended = attendedMap[e.event_id] === true;
                                                    const isPerformed = performedMap[e.event_id] === true;
                                                    const isWorked = workedMap[e.event_id] === true;
                                                    const isToggling = togglingMap[e.event_id];
                                                    return (
                                                        <tr key={e.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                                            <td style={styles.td}>
                                                                <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                    {formattedDate}
                                                                </Link>
                                                            </td>
                                                            <td style={styles.td}>{e.city || '—'}</td>
                                                            <td style={styles.td}>{e.state || '—'}</td>
                                                            <td style={styles.td}>{e.country || '—'}</td>
                                                            <td style={styles.td}>
                                                                <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                    {e.venue_name}
                                                                </Link>
                                                            </td>
                                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                                {e.artists || '—'}
                                                            </td>
                                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                {isPerformed ? (
                                                                    <span style={styles.performedBadge}>Performed</span>
                                                                ) : isWorked ? (
                                                                    <span style={styles.workedBadge}>Worked</span>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleToggleAttended(e.event_id)}
                                                                        disabled={isToggling}
                                                                        style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                                    >
                                                                        {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )
                    )}
                    {activeTab === 1 && (
                        festival.is_parent ? (
                            <SeriesFlyerHistoryTab childFestivals={child_festivals} />
                        ) : (
                            <FlyerHistoryTabs
                                entityType="festival"
                                entityId={festival.id}
                                shows={events}
                                searchByOptions={['Artist', 'Venue']}
                                multiEventTabLabel="Festival Flyer History"
                                showFlyerHistoryLabel="Show Flyer History"
                            />
                        )
                    )}
                    {/* ── Child Festivals tab ── */}
                    {activeTab === 2 && (
                        child_festivals.length === 0 ? (
                            <div style={styles.emptyTab}>No child festivals linked to this series yet.</div>
                        ) : (
                            <div style={styles.tableContainer}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}></th>
                                            <th style={styles.th}>Festival</th>
                                            <th style={styles.th}>Dates</th>
                                            <th style={styles.th}>Events</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {child_festivals.map((cf, idx) => {
                                            const isExpanded = expandedChildFestival === cf.id;
                                            const cfEvents = (child_festival_events || []).filter(e => e.festival_id === cf.id);
                                            const cfStart = formatDate(cf.start_date);
                                            const cfEnd = formatDate(cf.end_date);
                                            const cfDateRange = cfStart && cfEnd && cfStart !== cfEnd
                                                ? `${cfStart} – ${cfEnd}`
                                                : cfStart || '—';
                                            return (
                                                <React.Fragment key={cf.id}>
                                                    <tr
                                                        style={{ ...(idx % 2 === 0 ? styles.trEven : styles.trOdd), cursor: cfEvents.length > 0 ? 'pointer' : 'default' }}
                                                        onClick={() => cfEvents.length > 0 && setExpandedChildFestival(isExpanded ? null : cf.id)}
                                                    >
                                                        <td style={{ ...styles.td, width: '28px', color: '#bb86fc', fontSize: '12px' }}>
                                                            {cfEvents.length > 0 ? (isExpanded ? '▾' : '▸') : ''}
                                                        </td>
                                                        <td style={styles.td}>
                                                            <Link to={`/festivals/${cf.slug || cf.id}`} style={styles.tableLink} onClick={e => e.stopPropagation()}>
                                                                {cf.name}
                                                            </Link>
                                                        </td>
                                                        <td style={styles.td}>{cfDateRange}</td>
                                                        <td style={styles.td}>
                                                            <span style={styles.badge}>{cf.event_count}</span>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={4} style={{ padding: '0', backgroundColor: '#131313', borderBottom: '1px solid #333' }}>
                                                                <table style={{ ...styles.table, margin: 0 }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#1a1a1a' }}>
                                                                            <th style={{ ...styles.th, paddingLeft: '40px' }}>Date</th>
                                                                            <th style={styles.th}>City</th>
                                                                            <th style={styles.th}>State</th>
                                                                            <th style={styles.th}>Country</th>
                                                                            <th style={styles.th}>Venue</th>
                                                                            <th style={styles.th}>Artists</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {cfEvents.map((e, ei) => (
                                                                            <tr key={e.event_id} style={{ backgroundColor: ei % 2 === 0 ? '#161616' : '#131313' }}>
                                                                                <td style={{ ...styles.td, paddingLeft: '40px' }}>
                                                                                    <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                                        {formatDate(e.event_date)}
                                                                                    </Link>
                                                                                </td>
                                                                                <td style={styles.td}>{e.city || '—'}</td>
                                                                                <td style={styles.td}>{e.state || '—'}</td>
                                                                                <td style={styles.td}>{e.country || '—'}</td>
                                                                                <td style={styles.td}>
                                                                                    <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                                        {e.venue_name}
                                                                                    </Link>
                                                                                </td>
                                                                                <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{e.artists || '—'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    pendingDeletionBanner: {
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        backgroundColor: '#2c1818', border: '1px solid #e57373', borderRadius: '6px',
        padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#e0a0a0', lineHeight: 1.5
    },
    pendingDeletionIcon: { fontSize: '16px', lineHeight: 1.4, flexShrink: 0 },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    festivalImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    dateRange: { fontSize: '18px', color: '#bb86fc', marginBottom: '8px', fontWeight: '500' },
    parentRow: { fontSize: '14px', color: '#aaa', marginTop: '2px', marginBottom: '4px' },
    badge: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    promoterRow: { fontSize: '14px', color: '#aaa', marginTop: '4px' },
    promoterLink: { color: '#bb86fc', textDecoration: 'none' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px', width: '100%', minWidth: 0 },
    tabBar: { display: 'flex', borderBottom: '2px solid #2a2a2a' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px', width: '100%', minWidth: 0, boxSizing: 'border-box' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
    collapsibleSection: { marginBottom: '32px' },
    collapsibleHeader: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '14px 20px', cursor: 'pointer', color: '#e0e0e0' },
    collapsibleTitle: { fontSize: '16px', fontWeight: '600' },
    collapsibleChevron: { fontSize: '12px', color: '#888' },
    collapsibleBody: { backgroundColor: '#1a1a1a', border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px' },
    photosEmpty: { textAlign: 'center', color: '#666', padding: '20px 0', fontSize: '14px' },
    uploadLink: { backgroundColor: '#bb86fc', color: '#121212', borderRadius: '6px', padding: '7px 16px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
    addPhotoBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    removeDefaultBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '8px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.3) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
//
// NOTE: statsGrid and photoGrid already use responsive auto-fill and need no
// mobile override (confirmed during audit — effectively single-column at
// phone widths already).
const Smobile = {
    // Tab bar had no overflow handling at all — add horizontal scroll to
    // match the pattern ArtistDetail.js/VenueDetail.js already use.
    tabBar: {
        overflowX: 'auto',
    },
    // ArtistDetail.js/VenueDetail.js's working tab buttons rely on
    // whiteSpace: 'nowrap' so they scroll cleanly instead of the text
    // wrapping inside a shrunken button — matching that here too.
    tabBtn: {
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
};

export default FestivalDetail;
